import React, { useMemo, useRef, useState } from 'react';
import { TextField } from '@mui/material';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';

export const SMS_KEYWORDS = [
  { name: 'Voucher No', keyword: '@VoucherNo' },
  { name: 'Driver Name', keyword: '@DriverName' },
  { name: 'Driver Phone No', keyword: '@DriverPhoneNo' },
  { name: 'OTP', keyword: '@OTP' },
  { name: 'Delivery Date', keyword: '@DeliveryDate' },
  { name: 'Status', keyword: '@Status' },
  { name: 'Money', keyword: '@Money' },
  { name: 'Order Count', keyword: '@OrderCount' },
  { name: 'Salutation', keyword: '@Name' },
  { name: 'New Line', keyword: '%0A' },
  { name: 'Reason', keyword: '@Reason' },
  { name: 'Client Name', keyword: '@ClientName' },
  { name: 'Customer Name', keyword: '@CustomerName' },
  { name: 'Customer Amount', keyword: '@CustomerMoney' },
  { name: 'Link', keyword: '@LINK' },
  { name: 'Client Amount', keyword: '@ClientMoney' },
  { name: 'Client Area', keyword: '@ClientArea' },
  { name: 'Client Location', keyword: '@ClientLoc' },
  { name: 'Client Emirate', keyword: '@ClientEmirate' },
  { name: 'Customer Area', keyword: '@CustomerArea' },
  { name: 'Customer Location', keyword: '@CustomerLocation' },
  { name: 'Customer Emirate', keyword: '@CustomerEmirate' },
  { name: 'Customer Mobile', keyword: '@CustomerMobile' },
  { name: 'Attempts', keyword: '@Attempts' },
  { name: 'Company Name', keyword: '@companyName' },
  { name: 'Pickup Date', keyword: '@PickupDate' },
  { name: 'Client Mobile No', keyword: '@ClientMobileNo' },
];

const getActiveToken = (value, selectionStart) => {
  const beforeCursor = value.slice(0, selectionStart);
  const match = beforeCursor.match(/@[\w]*$/);
  if (!match) return null;

  return {
    token: match[0],
    start: selectionStart - match[0].length,
    end: selectionStart,
  };
};

const SmsKeywordTextField = ({ value, onValueChange, onChange, helperText, sx, ...props }) => {
  const inputRef = useRef(null);
  const [activeToken, setActiveToken] = useState(null);

  const suggestions = useMemo(() => {
    if (!activeToken) return [];
    const token = activeToken.token.toLowerCase();
    return SMS_KEYWORDS.filter((item) => item.keyword.toLowerCase().startsWith(token)).slice(0, 8);
  }, [activeToken]);

  const updateToken = (nextValue, selectionStart) => {
    setActiveToken(getActiveToken(nextValue, selectionStart));
  };

  const emitChange = (nextValue, originalEvent) => {
    if (onValueChange) {
      onValueChange(nextValue);
      return;
    }

    if (onChange) {
      onChange({
        ...originalEvent,
        target: {
          ...(originalEvent?.target || {}),
          value: nextValue,
        },
      });
    }
  };

  const handleChange = (event) => {
    const nextValue = event.target.value;
    emitChange(nextValue, event);
    updateToken(nextValue, event.target.selectionStart || nextValue.length);
  };

  const handleKeyUp = (event) => {
    updateToken(event.target.value, event.target.selectionStart || event.target.value.length);
  };

  const insertKeyword = (keyword) => {
    const input = inputRef.current;
    const currentValue = value || '';
    const token = activeToken || getActiveToken(currentValue, input?.selectionStart || currentValue.length);
    if (!token) return;

    const nextValue = `${currentValue.slice(0, token.start)}${keyword} ${currentValue.slice(token.end)}`;
    const nextCursor = token.start + keyword.length + 1;
    emitChange(nextValue);
    setActiveToken(null);

    requestAnimationFrame(() => {
      if (input) {
        input.focus();
        input.setSelectionRange(nextCursor, nextCursor);
      }
    });
  };

  const listOpen = Boolean(activeToken) && suggestions.length > 0;
  const nav = useListKeyboardNav({
    isOpen: listOpen,
    itemCount: suggestions.length,
    onSelectIndex: (index) => suggestions[index] && insertKeyword(suggestions[index].keyword),
    onClose: () => setActiveToken(null),
    resetKey: activeToken?.token || '',
  });

  return (
    <div className="relative">
      <TextField
        {...props}
        value={value}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        onKeyDown={nav.onKeyDown}
        onBlur={() => setTimeout(() => setActiveToken(null), 160)}
        inputRef={inputRef}
        helperText={helperText || 'Type @ to insert a keyword. Use arrows + Enter to select.'}
        sx={sx}
      />
      {listOpen && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl" role="listbox">
          {suggestions.map((item, index) => (
            <button
              key={item.keyword}
              type="button"
              role="option"
              aria-selected={index === nav.highlightedIndex}
              ref={(node) => nav.setItemRef(index, node)}
              onMouseEnter={() => nav.setHighlightedIndex(index)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertKeyword(item.keyword)}
              className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left ${
                index === nav.highlightedIndex ? 'bg-green-50' : 'hover:bg-green-50'
              }`}
            >
              <span className="text-sm font-medium text-gray-800">{item.name}</span>
              <span className="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-600">{item.keyword}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SmsKeywordTextField;
